/**
 * Health page (สุขภาพระบบ) — contract of /api/v1/health* (api/app/health/, docs/health.md).
 * Owner + head only; staff get 403. The server runs every check on a schedule, keeps the latest result per
 * check and the status changes of the last 7 days; "down" = fail twice in a row, cleared by ok/warn once.
 */

export type HealthStatus = 'ok' | 'warn' | 'fail' | 'unknown'

/** Group keys in their fixed order (the API orders groups worst-first, then this order). */
export const HEALTH_GROUPS = ['chat', 'channels', 'ai', 'web', 'server', 'sms', 'delivery', 'payment', 'alerts'] as const
export type HealthGroupKey = (typeof HEALTH_GROUPS)[number]

/** One point of the 24 h timeline: the status from `at` until the next point (or now). */
export interface HealthPoint {
  at: string
  status: HealthStatus
}

export interface HealthCheck {
  key: string
  group: string
  name: string
  status: HealthStatus
  /** fail two runs in a row; cleared by one ok/warn */
  down: boolean
  down_since: string | null
  latency_ms: number | null
  checked_at: string | null
  last_ok_at: string | null
  detail: string
  /** "what to do" in Thai */
  hint: string
  interval_s: number
  /** ascending; first = status in effect 24 h ago (or the first run if later); [] = never checked */
  timeline: HealthPoint[]
}

export interface HealthCounts {
  ok: number
  warn: number
  fail: number
  unknown: number
}

export interface HealthGroup {
  key: string
  name: string
  worst: HealthStatus
  counts: HealthCounts
  /** worst-first, then by name */
  checks: HealthCheck[]
}

export type HealthIncidentKind = 'down' | 'recovered' | 'change'

export interface HealthIncident {
  id: number
  key: string
  name: string
  group: string
  kind: HealthIncidentKind
  from: HealthStatus | null
  to: HealthStatus
  at: string
  detail: string
}

export interface HealthRunInfo {
  last_run_at: string | null
  /** when "ตรวจตอนนี้" is allowed again (null = now) */
  manual_next_at: string | null
  running: boolean
}

export interface HealthSettings {
  telegram_on: boolean
  /** bot token + chat id are set on the server */
  telegram_ready: boolean
  /** owner only */
  can_edit: boolean
}

export interface HealthSummaryCounts extends HealthCounts {
  down: number
  total: number
}

export interface HealthData {
  now: string
  checked_at: string | null
  run: HealthRunInfo
  summary: HealthSummaryCounts
  settings: HealthSettings
  groups: HealthGroup[]
  /** last 24 h, newest first, max 50 */
  incidents: HealthIncident[]
}

export interface HealthDownItem {
  key: string
  name: string
  since: string | null
}

/** GET /api/v1/health/summary — the app banner (polled every 60 s by owner/head). */
export interface HealthSummary {
  down: number
  down_items: HealthDownItem[]
  fail: number
  warn: number
  checked_at: string | null
}

export interface HealthApi {
  get(): Promise<HealthData>
  summary(): Promise<HealthSummary>
  /** "ตรวจตอนนี้": resolves after the run with fresh data · 429 → HealthRateLimited · 409 → ApiError */
  run(): Promise<HealthData>
  saveSettings(body: { telegram_on: boolean }): Promise<HealthSettings>
}
